import { Pipe, PipeTransform } from '@angular/core';

/**
 * The small slice of Markdown a project's `detailedDescription` is written in.
 *
 * It is typed into a plain textarea in the admin modal, so the formatting has
 * to be something a person can type: a blank line starts a new paragraph, a
 * single newline is a line break, and **this** is bold. Nothing else is
 * interpreted.
 *
 * Only that field. `description` is the carousel hover text and stays plain.
 */

const ESCAPES: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};

function escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, character => ESCAPES[character]);
}

/** Non-greedy, so two bold runs on one line stay two runs rather than one
 *  spanning the gap between them. An unpaired `**` matches nothing and is
 *  left as typed. */
function bold(value: string): string {
    return value.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}

function paragraphsOf(value: string): string[] {
    return value.replace(/\r\n?/g, '\n').trim().split(/\n{2,}/);
}

/**
 * Renders that Markdown to HTML for `[innerHTML]`.
 *
 * The source is escaped before anything else runs, so a description that
 * contains markup shows that markup as text instead of behaving as markup.
 * The only tags in the result are the ones built here — Angular's sanitizer
 * passes them through untouched, and there is nothing else for it to strip.
 */
@Pipe({ name: 'richText' })
export class RichTextPipe implements PipeTransform {

    transform(value: string | null | undefined): string {
        if (!value?.trim()) {
            return '';
        }

        return paragraphsOf(value)
            .map(paragraph => `<p>${bold(escapeHtml(paragraph)).replace(/\n/g, '<br>')}</p>`)
            .join('');
    }
}
